import React, { useContext, useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { toast } from "react-toastify";
import { useMutation } from "@tanstack/react-query";
import {
  Button,
  NumberController,
  RadioController,
  PanelModal,
} from "@pushpress/shared-components";
import { Card } from "@pushpress/pushpress-ui";
import {
  AppointmentPackage,
  postAppointmentCreditPurchase,
} from "../../../apis/appointmentApi";
import { useAppointmentTypesQuery } from "../../../apis/appointmentApi.hooks";
import AppointmentPackageSelect from "../../../components/controllers/AppointmentPackageSelect";
import AppointmentDiscountSelect from "../../../components/controllers/AppointmentDiscountSelect";
import AppointmentTypeSelect from "../../../components/controllers/AppointmentTypeSelect";
import TaxSelect from "../../../components/controllers/Deprecated/TaxSelect";
import UserBillingSelect from "../../../components/controllers/UserBillingSelect";
import MemberSelect from "../../../components/controllers/Deprecated/MemberSelect";
import TextChangingButton from "../../../components/inputs/TextChangeLoadingButton";
import { AppContext } from "../../../context/app";
import { defaultToastOptions, extractError } from "../../../utils/misc";
import { sortByName } from "../../../utils/sortUtils";
import PackageTotals from "./PackageTotals";
import CustomTotals from "./CustomTotals";
import { CreditsForm } from "./CreditsModal.types";
import SessionsCredit from "./SessionsCredit";
import {
  calculateCustomTotals,
  calculatePackageTotals,
  convertCreditsFormToPayload,
  sessionTypeOptions,
  timeOptions,
} from "./CreditsModal.utils";
import { getCurrencySymbol } from "../../../utils/currencyUtils";
import {
  useGetInvoicePreviewForAppointmentCreditsQuery,
  useGetInvoicePreviewForAppointmentPackageQuery,
} from "./CreditsModal-generated.hooks";
import { getBillingUuid } from "../../../components/controllers/UserBillingSelect/UserBillingSelect.utils";
import { Info } from "lucide-react";

interface CreditsModalProps {
  userUuid?: string;
  appointmentTypeUuid?: string;
  visible: boolean;
  onClose: VoidFunction;
  onSuccess: () => void;
}

type InputOption<T> = {
  data: T;
};

function CreditsModal(props: CreditsModalProps) {
  const { visible, onClose } = props;

  return (
    <PanelModal
      width="400px"
      visible={visible}
      closeModal={onClose}
      verticalAlign="top"
      disableClickAway
      disableEscapeKeyDown
    >
      <CreditsModalContent {...props} />
    </PanelModal>
  );
}

function CreditsModalContent(props: CreditsModalProps) {
  const { userUuid, appointmentTypeUuid, onClose, onSuccess } = props;

  const app = useContext(AppContext);

  const [saving, setSaving] = useState(false);

  const { control, handleSubmit, setValue, resetField } = useForm<CreditsForm>({
    mode: "onTouched",
    defaultValues: {
      creditsExpireType: timeOptions[0],
      creditsExpireValue: 30,
    },
  });

  const [
    sessionType,
    appointmentType,
    appointmentPackage,
    appointmentDiscount,
    price,
    tax,
    member,
    billing,
  ] = useWatch({
    control,
    name: [
      "sessionType",
      "appointmentType",
      "appointmentPackage",
      "appointmentDiscount",
      "price",
      "tax",
      "member",
      "billing",
    ],
  });

  const {
    data: invoicePreviewForPackageQuery,
    loading: loadingInvoicePreviewForPackage,
  } = useGetInvoicePreviewForAppointmentPackageQuery({
    variables: {
      getInvoicePreviewForAppointmentPackageInput: {
        billingUuid: getBillingUuid(billing?.uuid),
        appointmentPackageUuid: appointmentPackage?.uuid,
        discountUuid: appointmentDiscount?.uuid
          ? String(appointmentDiscount?.uuid)
          : undefined,
        source: "ControlPanel",
      },
    },
    skip: sessionType !== "PACKAGE" || !appointmentPackage?.uuid,
  });

  const {
    data: invoicePreviewCreditsQuery,
    loading: loadingInvoicePreviewForCredit,
  } = useGetInvoicePreviewForAppointmentCreditsQuery({
    variables: {
      getInvoicePreviewForAppointmentCreditsInput: {
        billingUuid: billing?.uuid,
        amount: price,
        taxUuid: tax?.uuid,
        discountUuid: appointmentDiscount?.uuid,
      },
    },
    skip: sessionType !== "CREDIT",
  });

  const appointmentTypesQuery = useAppointmentTypesQuery();

  const purchaseMutation = useMutation(postAppointmentCreditPurchase);

  const packageTotals = calculatePackageTotals(
    invoicePreviewForPackageQuery?.getInvoicePreviewForAppointmentPackage,
  );

  const customTotals = calculateCustomTotals(
    invoicePreviewCreditsQuery?.getInvoicePreviewForAppointmentCredits,
  );

  const appTypeUuid = appointmentTypeUuid || appointmentType?.uuid;

  const save = async (values: CreditsForm) => {
    try {
      setSaving(true);
      const userId = userUuid || values.member.uuid;
      const payload = convertCreditsFormToPayload(userId, values, appTypeUuid);
      await purchaseMutation.mutateAsync({
        clientUuid: app.client!.uuid,
        payload,
      });
      toast.success("Credits purchased successfully", defaultToastOptions);
      onSuccess();
    } catch (error) {
      toast.error(extractError(error), defaultToastOptions);
    } finally {
      setSaving(false);
    }
  };

  const handleMemberChange = () => {
    resetField("billing");
  };

  const handleAppointmentTypeChange = () => {
    resetField("appointmentPackage");
  };

  const handleOk = handleSubmit(save);

  const filterPackageOption = (
    option: InputOption<AppointmentPackage>,
    inputValue: string,
  ) => {
    const hasTheSameAppointmentType =
      option.data.appointmentType.uuid === appTypeUuid;
    const containsTheFilter = inputValue
      ? option.data.name.toLowerCase().includes(inputValue.toLowerCase())
      : true;
    return hasTheSameAppointmentType && containsTheFilter;
  };

  const getOptionGroup = (option: AppointmentPackage) => {
    return option.appointmentType?.name;
  };

  useEffect(() => {
    if (appointmentTypesQuery?.data?.data) {
      const data = [...appointmentTypesQuery.data.data].sort(sortByName);
      const appointmentTypesWithStaff = data.filter(
        (item) => item.staff.length > 0,
      );
      if (appointmentTypesWithStaff.length > 0) {
        setValue("appointmentType", appointmentTypesWithStaff[0]);
      }
    }
  }, [appointmentTypesQuery?.data, setValue]);

  const isPaid =
    sessionType === "PACKAGE"
      ? !!packageTotals?.totalValue
      : !!customTotals?.totalValue;
  const isPackageNotFree =
    sessionType === "PACKAGE" && appointmentPackage?.packagePrice > 0;
  const isPackageTotalFree =
    sessionType === "PACKAGE" && packageTotals?.totalValue === 0;
  const currencySymbol = getCurrencySymbol(app.client!.currencyIso);

  return (
    <>
      <PanelModal.Header title="Add credits">
        <Button
          text="Close"
          buttonType="secondary"
          size="medium"
          onClick={onClose}
        />
        <TextChangingButton
          text={isPaid ? "Buy" : "Add"}
          buttonType="primary"
          size="medium"
          loading={saving}
          onClick={handleOk}
        />
      </PanelModal.Header>
      <PanelModal.Body paddingSize="none">
        <div className="tw-pt-4 tw-pr-4 tw-pb-0 tw-pl-4">
          <div className="tw-pb-4">
            <Card className="tw-p-2">
              <div className="tw-flex tw-items-center tw-gap-2">
                <Info className="tw-w-4 tw-h-4 tw-text-gray-600" />
                <p className="tw-text-sm tw-text-gray-600">
                  Sell credits that members can use to book appointments.
                </p>
              </div>
            </Card>
          </div>
          {!userUuid && (
            <MemberSelect
              label="Client"
              placeholder="Search for a member or lead"
              control={control}
              name="member"
              required
              hideDropdownIndicator
              isClearable
              onChange={handleMemberChange}
            />
          )}
          {!appointmentTypeUuid && (
            <AppointmentTypeSelect
              control={control}
              name="appointmentType"
              label="Appointment Type"
              required
              placeholder="-Select-"
              onChange={handleAppointmentTypeChange}
            />
          )}
          {!userUuid && (
            <SessionsCredit
              userUuid={member?.uuid}
              appointmentTypeUuid={appointmentType?.uuid}
            />
          )}
          <div data-cy="session-type">
            <p className="tw-text-xs tw-text-gray-600 tw-mb-2">
              Select the type of credit *
            </p>
            <RadioController
              options={sessionTypeOptions}
              direction="vertical"
              control={control}
              name="sessionType"
              label="Credit type"
              hideLabel
              required
            />
          </div>
          {sessionType === "PACKAGE" && (
            <>
              <div data-cy="package-select">
                <AppointmentPackageSelect
                  label="Package"
                  control={control}
                  name="appointmentPackage"
                  required
                  filterOption={filterPackageOption}
                  getOptionGroup={getOptionGroup}
                  placeholder="-Select-"
                />
              </div>
              {isPackageNotFree && (
                <>
                  <AppointmentDiscountSelect
                    label="Discount"
                    control={control}
                    name="appointmentDiscount"
                    isClearable
                    hideDropdownIndicator
                    placeholder="-Add-"
                  />
                  {!isPackageTotalFree && (
                    <UserBillingSelect
                      label="Payment method"
                      control={control}
                      name="billing"
                      required
                      userUuid={userUuid || member?.uuid}
                      placeholder="-Select-"
                    />
                  )}
                </>
              )}
            </>
          )}
          {sessionType === "CREDIT" && (
            <>
              <NumberController
                label="Number of credits"
                control={control}
                name="numberOfCredits"
                required
                min={1}
              />
              <NumberController
                label="Credits expire after"
                control={control}
                name="creditsExpireValue"
                required
                min={1}
                maxDecimalDigits={0}
                endOuterText="day(s)"
                inputWidth="100px"
              />
              <NumberController
                label="Package price"
                control={control}
                name="price"
                required
                startText={currencySymbol}
                helperText="If free, enter $0"
                min={0}
              />
              {!!price && price > 0 && (
                <>
                  <TaxSelect
                    label="Tax"
                    control={control}
                    name="tax"
                    isClearable
                    placeholder="-Add-"
                    hideDropdownIndicator
                  />
                  <UserBillingSelect
                    label="Payment method"
                    control={control}
                    name="billing"
                    required
                    placeholder="-Select-"
                    userUuid={userUuid || member?.uuid}
                  />
                </>
              )}
            </>
          )}
        </div>
        {sessionType === "PACKAGE" && appointmentPackage?.uuid && (
          <PackageTotals
            packageTotals={packageTotals}
            loading={loadingInvoicePreviewForPackage}
          />
        )}
        {sessionType === "CREDIT" && String(price).length > 0 && (
          <CustomTotals
            customTotals={customTotals}
            loading={loadingInvoicePreviewForCredit}
          />
        )}
      </PanelModal.Body>
    </>
  );
}

export default CreditsModal;
